import { useId } from 'react';

import { tv } from '@/lib/tv';

const goodbyeSunsetStyles = tv({
  slots: {
    root: 'block aspect-[640/260] w-full shrink-0',
    farRidge: 'fill-primary-purple-700',
    dusk: 'fill-primary-purple-900 opacity-0 animate-scroll-dusk',
    sun: 'animate-scroll-sun-set',
    glow: 'animate-scroll-glow-fade',
    nearRidge: 'fill-primary-purple-800',
    ground: 'fill-primary-purple-900',
    silhouette: 'fill-primary-purple-900',
    bird: 'fill-none stroke-primary-purple-900 opacity-50',
    star: 'fill-primary-beige-100',
  },
});

const STARS = [
  { cx: 72, cy: 34, r: 1.2, opacity: 0.7 },
  { cx: 168, cy: 18, r: 0.9, opacity: 0.5 },
  { cx: 260, cy: 44, r: 0.8, opacity: 0.4 },
  { cx: 512, cy: 26, r: 1.1, opacity: 0.6 },
  { cx: 590, cy: 58, r: 0.8, opacity: 0.4 },
];

const SUN_STRIPES = [
  { y: 172, height: 3 },
  { y: 181, height: 4 },
  { y: 191, height: 5 },
  { y: 202, height: 6 },
];

const GoodbyeSunset = () => {
  const styles = goodbyeSunsetStyles();
  const id = useId();
  const skyId = `${id}-sky`;
  const sunId = `${id}-sun`;
  const glowId = `${id}-glow`;
  const stripesId = `${id}-stripes`;

  return (
    <svg
      aria-hidden
      viewBox="0 0 640 260"
      preserveAspectRatio="xMidYMax slice"
      className={styles.root()}
    >
      <defs>
        <linearGradient id={skyId} x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0"
            style={{ stopColor: 'var(--color-primary-purple-900)' }}
          />
          <stop
            offset="0.3"
            style={{ stopColor: 'var(--color-primary-purple-700)' }}
          />
          <stop
            offset="0.55"
            style={{ stopColor: 'var(--color-secondary-purple)' }}
          />
          <stop
            offset="0.72"
            style={{ stopColor: 'var(--color-secondary-pink)' }}
          />
          <stop
            offset="0.82"
            style={{ stopColor: 'var(--color-secondary-orange)' }}
          />
          <stop
            offset="1"
            style={{ stopColor: 'var(--color-secondary-yellow)' }}
          />
        </linearGradient>
        <linearGradient id={sunId} x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0"
            style={{ stopColor: 'var(--color-secondary-yellow)' }}
          />
          <stop
            offset="1"
            style={{ stopColor: 'var(--color-secondary-orange)' }}
          />
        </linearGradient>
        <radialGradient id={glowId}>
          <stop
            offset="0"
            style={{
              stopColor: 'var(--color-secondary-yellow)',
              stopOpacity: 0.55,
            }}
          />
          <stop
            offset="1"
            style={{
              stopColor: 'var(--color-secondary-yellow)',
              stopOpacity: 0,
            }}
          />
        </radialGradient>
        <mask id={stripesId}>
          <rect width="640" height="260" fill="white" />
          {SUN_STRIPES.map((stripe) => (
            <rect
              key={stripe.y}
              x="0"
              y={stripe.y}
              width="640"
              height={stripe.height}
              fill="black"
            />
          ))}
        </mask>
      </defs>

      <rect width="640" height="260" fill={`url(#${skyId})`} />
      <rect className={styles.dusk()} width="640" height="260" />

      {STARS.map((star) => (
        <circle
          key={`${star.cx}-${star.cy}`}
          className={styles.star()}
          cx={star.cx}
          cy={star.cy}
          r={star.r}
          opacity={star.opacity}
        />
      ))}

      <g className={styles.sun()}>
        <circle
          className={styles.glow()}
          cx="320"
          cy="214"
          r="150"
          fill={`url(#${glowId})`}
        />
        <circle
          cx="320"
          cy="214"
          r="74"
          fill={`url(#${sunId})`}
          mask={`url(#${stripesId})`}
        />
      </g>

      <path
        className={styles.bird()}
        strokeWidth="1.6"
        strokeLinecap="round"
        d="M188 88 q5 -5 10 0 q5 -5 10 0 M216 102 q4 -4 8 0 q4 -4 8 0"
      />

      <path
        className={styles.farRidge()}
        d="M0 206 L58 206 L70 186 L138 186 L150 206 L404 206 L418 180 L470 180 L476 170 L520 170 L528 180 L540 206 L640 206 V260 H0 Z"
      />
      <path
        className={styles.nearRidge()}
        d="M0 222 C90 210 190 214 300 218 C410 222 520 208 640 214 V260 H0 Z"
      />
      <path
        className={styles.ground()}
        d="M0 236 C120 226 250 230 380 232 C470 234 560 226 640 230 V260 H0 Z"
      />

      <path
        className={styles.silhouette()}
        d="M86 232 V196 a5 5 0 0 1 10 0 V214 h6 V204 a4 4 0 0 1 8 0 V218 a4 4 0 0 1 -4 4 H96 V232 Z M86 210 H80 a4 4 0 0 1 -4 -4 V198 a4 4 0 0 1 8 0 V204 H86 Z"
      />

      <g
        className={styles.silhouette()}
        transform="translate(452 186) scale(0.9)"
      >
        <path d="M10 26 C4 28 2 36 4 42 C6 36 9 31 13 29 L14 34 L13 50 L16 50 L17 36 L20 36 L21 50 L24 50 L24 35 L36 35 L37 50 L40 50 L40 35 L42 34 L44 50 L47 50 L45 32 C47 28 49 24 52 21 L58 24 L60 21 L55 15 L54 12 L52 14 C46 16 42 20 40 22 C30 21 18 21 13 23 Z" />
        <path d="M27 23 L29 12 C30 10 34 10 35 12 L36 23 Z M30 22 L33 31 L35 31 L34 22 Z" />
        <circle cx="32" cy="8" r="2.6" />
        <path d="M26.5 6.2 L37.5 6.2 L36 5.2 L34.5 5.2 L34 2 L30 2 L29.5 5.2 L28 5.2 Z" />
      </g>
    </svg>
  );
};

export default GoodbyeSunset;
